// biome-ignore-all lint/suspicious/noArrayIndexKey: index keys are safe here; lists are derived from render state in a stable order and items are not reordered or removed mid-render (Task #32).
// =============================================================================
// UniformTable.jsx — table rendering uniform item rows (warm/cold split)
// =============================================================================
// Renders: a grid of uniform items (shirts, pants, jumpers, etc.) for one
//          child, with quantity inputs. When isCold is true, also shows the
//          winter-weather items (jacket, beanie, etc.).
// Props:
//   child    - child object with `items` map (item key → quantity)
//   onChange - called with updated item-quantity map
//   showSku  - if true, show supplier SKUs alongside item names (staff view)
//   isCold   - if true, render the cold-climate item rows too
//   locked   - if true, render quantities as read-only
//   showAll  - if true, render every item even when quantity is 0 (default
//              hides 0-qty rows in read-only modes)
// Notes: item catalogue + cold-climate flag come from UNIFORM_ITEMS / WINTER_ITEMS
//        in formConstants.
// =============================================================================

import React from "react";
import { customUniformRowCounts, UNIFORM_ITEMS, WINTER_ITEMS } from "../../utils/formConstants";
import { money } from "../../utils/helpers";

function UniformTable({ child, onChange, showSku, isCold, locked, showAll, requireCustomName }) {
  const items = child.items || {};
  const customItems = child.customItems || [
    { name: "", sku: "", size: "", cost: "", qty: 0 },
    { name: "", sku: "", size: "", cost: "", qty: 0 },
    { name: "", sku: "", size: "", cost: "", qty: 0 },
    { name: "", sku: "", size: "", cost: "", qty: 0 },
    { name: "", sku: "", size: "", cost: "", qty: 0 },
  ];
  const allItems = isCold ? [...UNIFORM_ITEMS, ...WINTER_ITEMS] : UNIFORM_ITEMS;

  const displayItems =
    locked && !showAll
      ? allItems.filter(function (k) {
          const d = items[k] || {};
          return (d.qty || 0) > 0 && (parseFloat(d.cost) || 0) > 0;
        })
      : allItems;
  const displayCustom =
    locked && !showAll ? customItems.filter(customUniformRowCounts) : customItems;

  function upItem(k, val) {
    onChange(Object.assign({}, child, { items: Object.assign({}, items, { [k]: val }) }));
  }
  function upCustom(i, val) {
    onChange(
      Object.assign({}, child, {
        customItems: customItems.map(function (c, x) {
          return x === i ? val : c;
        }),
      })
    );
  }

  let total = 0;
  allItems.forEach(function (k) {
    const d = items[k] || {};
    total += (d.qty || 0) * (parseFloat(d.cost) || 0);
  });
  customItems.forEach(function (d) {
    if (!customUniformRowCounts(d)) return;
    total += (d.qty || 0) * (parseFloat(d.cost) || 0);
  });

  // Sequential pair-by-pair (used for the "Additional Items" rows).
  function pairs(arr) {
    const out = [];
    for (let i = 0; i < arr.length; i += 2)
      out.push([arr[i], i + 1 < arr.length ? arr[i + 1] : null]);
    return out;
  }
  // Column-major split (used for the standard items grid):
  // first half of the array is the LEFT column, second half is RIGHT.
  function colPairs(arr) {
    const half = Math.ceil(arr.length / 2);
    const out = [];
    for (let i = 0; i < half; i++) {
      out.push([arr[i], i + half < arr.length ? arr[i + half] : null]);
    }
    return out;
  }

  const inp = {
    padding: "4px 6px",
    borderRadius: 4,
    border: "1px solid #CFE0DE",
    fontSize: 12,
    fontFamily: "inherit",
    background: "#fff",
    boxSizing: "border-box",
    width: "100%",
  };
  const hTh = {
    fontSize: 11,
    fontWeight: 700,
    color: "#607D79",
    textTransform: "uppercase",
    letterSpacing: 0.4,
    padding: "6px 8px",
    background: "#F5F7F7",
    borderBottom: "2px solid #E0E0E0",
    whiteSpace: "nowrap",
  };
  const sep = { width: 2, background: "#E0E0E0", padding: 0 };

  function ItemCells(label, d, isCustom, onChg) {
    d = d || {};
    const sub = (d.qty || 0) * (parseFloat(d.cost) || 0);
    const hasVal = (d.qty || 0) > 0 && (parseFloat(d.cost) || 0) > 0;
    const bg = hasVal ? "#F1F8F6" : "transparent";
    const fw = hasVal ? 700 : 400;
    // Additional-item rows: the Cost + Qty inputs stay disabled until the row
    // has a name, so a priced-but-nameless line item can't be created (it would
    // count toward the total but never appear on the order). Standard items and
    // School Locker (SKU-keyed) rows are unaffected.
    const gateCustom =
      isCustom && !locked && requireCustomName && !(d.name && String(d.name).trim());
    const gatedInp = gateCustom
      ? { background: "#F5F5F5", color: "#BDBDBD", cursor: "not-allowed" }
      : null;
    return [
      React.createElement(
        "td",
        {
          style: {
            padding: "5px 8px",
            fontSize: 13,
            fontWeight: fw,
            background: bg,
            maxWidth: 120,
            minWidth: 90,
          },
        },
        isCustom && !locked
          ? React.createElement("input", {
              value: d.name || "",
              placeholder: "Item name",
              onChange: function (e) {
                onChg(Object.assign({}, d, { name: e.target.value }));
              },
              style: inp,
            })
          : label || d.name || "-"
      ),
      showSku &&
        React.createElement(
          "td",
          { style: { padding: "4px 6px", background: bg } },
          locked
            ? React.createElement(
                "span",
                { style: { fontSize: 12, color: "#546E7A" } },
                d.sku || "-"
              )
            : React.createElement("input", {
                value: d.sku || "",
                placeholder: "SKU",
                onChange: function (e) {
                  onChg(Object.assign({}, d, { sku: e.target.value }));
                },
                style: inp,
              })
        ),
      showSku &&
        React.createElement(
          "td",
          { style: { padding: "4px 6px", background: bg, textAlign: "center" } },
          locked
            ? React.createElement(
                "span",
                { style: { fontSize: 12, color: "#546E7A" } },
                d.size || "-"
              )
            : React.createElement("input", {
                value: d.size || "",
                placeholder: "Size",
                onChange: function (e) {
                  onChg(Object.assign({}, d, { size: e.target.value }));
                },
                style: Object.assign({}, inp, { textAlign: "center" }),
              })
        ),
      React.createElement(
        "td",
        { style: { padding: "4px 4px", background: bg, width: 58, textAlign: "right" } },
        locked
          ? React.createElement("span", { style: { fontSize: 12 } }, d.cost ? money(d.cost) : "-")
          : React.createElement("input", {
              type: "number",
              value: d.cost || "",
              placeholder: "0.00",
              disabled: gateCustom,
              title: gateCustom ? "Enter an item name first" : undefined,
              onChange: function (e) {
                onChg(Object.assign({}, d, { cost: e.target.value }));
              },
              style: Object.assign({}, inp, { textAlign: "right", padding: "4px 4px" }, gatedInp),
            })
      ),
      React.createElement(
        "td",
        { style: { padding: "4px 4px", background: bg, width: 46 } },
        locked
          ? React.createElement(
              "span",
              { style: { fontSize: 12, textAlign: "center", display: "block" } },
              d.qty || 0
            )
          : React.createElement(
              "select",
              {
                value: d.qty || 0,
                disabled: gateCustom,
                title: gateCustom ? "Enter an item name first" : undefined,
                onChange: function (e) {
                  onChg(Object.assign({}, d, { qty: parseInt(e.target.value, 10) }));
                },
                style: Object.assign({}, inp, { width: 46, padding: "4px 2px" }, gatedInp),
              },
              (label === null ||
              [
                "Sun-Safe Everyday Hat",
                "Sports Hat",
                "Backpack",
                "Tie",
                "Jumper",
                "Track Jacket",
                "Track Pants",
              ].indexOf(label) >= 0
                ? [0, 1]
                : [0, 1, 2, 3]
              ).map(function (n) {
                return React.createElement("option", { key: n, value: n }, n);
              })
            )
      ),
      React.createElement(
        "td",
        {
          style: {
            padding: "5px 8px",
            fontSize: 12,
            fontWeight: 700,
            color: sub > 0 ? "#1B6B5A" : "#CFD8DC",
            textAlign: "right",
            background: bg,
            width: 72,
            whiteSpace: "nowrap",
          },
        },
        sub > 0 ? money(sub) : "-"
      ),
    ].filter(Boolean);
  }

  function ColGroup() {
    if (!showSku)
      return React.createElement(
        "colgroup",
        null,
        React.createElement("col", { style: { width: "55%" } }),
        React.createElement("col", { style: { width: 75 } }),
        React.createElement("col", { style: { width: 58 } }),
        React.createElement("col", { style: { width: 72 } }),
        React.createElement("col", { style: { width: 2 } }),
        React.createElement("col", { style: { width: "55%" } }),
        React.createElement("col", { style: { width: 75 } }),
        React.createElement("col", { style: { width: 58 } }),
        React.createElement("col", { style: { width: 72 } })
      );
    return React.createElement(
      "colgroup",
      null,
      React.createElement("col", { style: { width: "15%" } }),
      React.createElement("col", { style: { width: "15%" } }),
      React.createElement("col", { style: { width: "7%" } }),
      React.createElement("col", { style: { width: 58 } }),
      React.createElement("col", { style: { width: 46 } }),
      React.createElement("col", { style: { width: 72 } }),
      React.createElement("col", { style: { width: 2 } }),
      React.createElement("col", { style: { width: "15%" } }),
      React.createElement("col", { style: { width: "15%" } }),
      React.createElement("col", { style: { width: "7%" } }),
      React.createElement("col", { style: { width: 58 } }),
      React.createElement("col", { style: { width: 46 } }),
      React.createElement("col", { style: { width: 72 } })
    );
  }

  // ── Live 3-day-rule validation ────────────────────────────────────────────
  // Mirrors the submit-time validation in RequestWizard.jsx so users (shelter
  // OR responder editing) see immediately when they've exceeded the cap.
  function qty(name) {
    const d = items[name] || {};
    return parseInt(d.qty, 10) || 0;
  }
  const dresses = qty("Dress");
  const topsCount = qty("Formal Shirt") + qty("Sports/Polo Shirt");
  const bottomsCount =
    qty("Formal Shorts") + qty("Formal Skirt") + qty("Sports Shorts") + qty("Sports Skirt/Skort");
  const topsMax = 3 - dresses;
  const bottomsMax = 3 - dresses;
  const liveWarnings = [];
  if (topsCount > topsMax) {
    liveWarnings.push(
      "Tops: " +
        topsCount +
        " selected, max " +
        topsMax +
        (dresses > 0 ? ` (3 minus ${dresses} dress${dresses === 1 ? "" : "es"})` : "")
    );
  }
  if (bottomsCount > bottomsMax) {
    liveWarnings.push(
      "Bottoms: " +
        bottomsCount +
        " selected, max " +
        bottomsMax +
        (dresses > 0 ? ` (3 minus ${dresses} dress${dresses === 1 ? "" : "es"})` : "")
    );
  }

  return (
    <div style={{ overflowX: "auto" }}>
      {!locked && liveWarnings.length > 0 && (
        <div
          style={{
            background: "#FFEBEE",
            border: "1px solid #EF9A9A",
            borderRadius: 7,
            padding: "8px 12px",
            marginBottom: 8,
            fontSize: 13,
            color: "#C62828",
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 2 }}>3-day supply rule exceeded</div>
          {liveWarnings.map(function (w, i) {
            return (
              <div key={i} style={{ fontSize: 12 }}>
                {`• ${w}`}
              </div>
            );
          })}
        </div>
      )}
      <table
        style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, tableLayout: "fixed" }}
      >
        <ColGroup />
        <thead>
          <tr>
            <th style={Object.assign({}, hTh, { textAlign: "left" })}>Item</th>
            {showSku && <th style={Object.assign({}, hTh, { textAlign: "left" })}>SKU</th>}
            {showSku && <th style={Object.assign({}, hTh, { textAlign: "center" })}>Size</th>}
            <th style={Object.assign({}, hTh, { textAlign: "right" })}>Cost</th>
            <th style={Object.assign({}, hTh, { textAlign: "center" })}>Qty</th>
            <th style={Object.assign({}, hTh, { textAlign: "right" })}>Total</th>
            <th style={sep}></th>
            <th style={Object.assign({}, hTh, { textAlign: "left" })}>Item</th>
            {showSku && <th style={Object.assign({}, hTh, { textAlign: "left" })}>SKU</th>}
            {showSku && <th style={Object.assign({}, hTh, { textAlign: "center" })}>Size</th>}
            <th style={Object.assign({}, hTh, { textAlign: "right" })}>Cost</th>
            <th style={Object.assign({}, hTh, { textAlign: "center" })}>Qty</th>
            <th style={Object.assign({}, hTh, { textAlign: "right" })}>Total</th>
          </tr>
        </thead>
        <tbody>
          {colPairs(displayItems).map(function (pair, pi) {
            const left = pair[0];
            const right = pair[1];
            const ld = items[left] || {};
            const rd = right ? items[right] || {} : null;
            return (
              <tr
                key={pi}
                style={{
                  borderBottom: "1px solid #ECEFF1",
                  background: pi % 2 === 0 ? "#fff" : "#FAFAFA",
                }}
              >
                {ItemCells(left, ld, false, function (v) {
                  upItem(left, v);
                })}
                <td style={sep}></td>
                {right
                  ? ItemCells(right, rd, false, function (v) {
                      upItem(right, v);
                    })
                  : [React.createElement("td", { key: "e1", colSpan: showSku ? 6 : 4 })]}
              </tr>
            );
          })}
          <tr>
            <td
              colSpan={showSku ? 13 : 9}
              style={{
                background: "#F5F7F7",
                borderTop: "2px solid #E0E0E0",
                padding: "5px 8px",
                fontSize: 11,
                fontWeight: 700,
                color: "#607D79",
                textTransform: "uppercase",
                letterSpacing: 0.4,
              }}
            >
              Additional Items
            </td>
          </tr>
          {pairs(
            displayCustom.map(function (_, i) {
              return i;
            })
          ).map(function (pair, pi) {
            const i0 = pair[0];
            const i1 = pair[1];
            // Render from displayCustom (the rows we actually show), NOT customItems.
            // In locked/read mode displayCustom is the FILTERED list, so indexing back
            // into the unfiltered customItems showed interleaved empty rows and dropped
            // trailing real rows — the visible lines then summed to LESS than the (correct)
            // TOTAL. That was the 2026-03024 "grand total vs sum of items" mismatch: users
            // fill the two-column grid down the LEFT column (array slots 0,2,4), so the
            // empties at 1,3 sit BETWEEN the real rows. In edit mode displayCustom ===
            // customItems, so upCustom(i) still targets the correct row (unchanged there).
            return (
              <tr
                key={`c${pi}`}
                style={{
                  borderBottom: "1px solid #ECEFF1",
                  background: pi % 2 === 0 ? "#fff" : "#FAFAFA",
                }}
              >
                {ItemCells(null, displayCustom[i0] || {}, true, function (v) {
                  upCustom(i0, v);
                })}
                <td style={sep}></td>
                {i1 !== null && i1 !== undefined
                  ? ItemCells(null, displayCustom[i1] || {}, true, function (v) {
                      upCustom(i1, v);
                    })
                  : [React.createElement("td", { key: "e2", colSpan: showSku ? 6 : 4 })]}
              </tr>
            );
          })}
        </tbody>
      </table>
      {total > 0 && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 16,
            marginTop: 8,
            paddingTop: 8,
            borderTop: "2px solid #00695C",
          }}
        >
          <span style={{ fontWeight: 700, fontSize: 14 }}>TOTAL (Excl. Shoes)</span>
          <span
            style={{
              fontSize: 18,
              fontWeight: 800,
              color: "#00695C",
              background: "#E0F2F1",
              borderRadius: 6,
              padding: "4px 14px",
            }}
          >
            {money(total)}
          </span>
        </div>
      )}
    </div>
  );
}

export { UniformTable };
